import * as React from 'react';
import type { DataTableProps } from './types';
import { cn } from '../../../lib/utils';
import { Skeleton } from '../../ui/skeleton';

type PaginationConfig = NonNullable<DataTableProps<unknown>['pagination']>;

interface DataTablePaginationProps {
  pagination: PaginationConfig;
}

/**
 * Windowed page list: first + last always shown, current ±1 in the middle,
 * `'ellipsis'` for the gaps. Tables with ≤7 pages show every page. Keeps the
 * control row compact regardless of how many pages a table has.
 */
function pageItems(current: number, totalPages: number): Array<number | 'ellipsis'> {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const items: Array<number | 'ellipsis'> = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(totalPages - 1, current + 1);
  if (start > 2) items.push('ellipsis');
  for (let p = start; p <= end; p += 1) items.push(p);
  if (end < totalPages - 1) items.push('ellipsis');
  items.push(totalPages);
  return items;
}

/** Page sizes for `total`: the standard sizes smaller than total, then total
 *  itself as the "show all" max — so the largest choice always shows every row
 *  and no size larger than the data is offered. The caller hides the control
 *  when fewer than two remain. */
function resolvePageSizeOptions(total: number, base: readonly number[], maxPageSize: number): number[] {
  const sizes = base.filter((size) => size < total && size <= maxPageSize);
  return total <= maxPageSize ? [...sizes, total] : sizes;
}

/** The option to show as selected: the current size when it is offered, else the
 *  largest offered size that still fits it — the effective size on screen — never
 *  the show-all max when the actual size is smaller.
 *
 *  This can INTENTIONALLY differ from the live `pageSize` state: when a filter
 *  shrinks `total` below a larger chosen size (e.g. size=100, total=40), the
 *  select shows the effective 40 while the caller keeps `pageSize` at 100. That
 *  is deliberate — reconciling the state down to 40 here would silently discard
 *  the user's 100-row preference the moment the filter is cleared and `total`
 *  grows back. The divergence is harmless: a larger-than-`total` request just
 *  returns every row, so what renders matches the shown size either way. The
 *  returned value is always one of `options`, so the controlled `<select>` never
 *  gets an out-of-range value. */
function selectedPageSize(options: number[], pageSize: number): number | undefined {
  if (options.includes(pageSize)) return pageSize;
  const fitting = options.filter((size) => size <= pageSize);
  if (fitting.length > 0) return fitting[fitting.length - 1];
  return options[0];
}

// Figma DS pagination control: 32×32 (size-8), radius 6 (rounded-md), 12px/500.
const CONTROL =
  'inline-flex size-8 items-center justify-center rounded-md text-[12px] font-medium leading-none transition-colors';
// Prev/next + inactive pages: 1px border, foreground glyph (Figma #E5E5E5 / #171717).
const BORDERED = 'border border-border text-foreground hover:bg-muted';

/**
 * Footer rendered below the table when the `pagination` prop is set, matched to
 * the Figma design-system pagination: "Showing X–Y of Z {noun}" on the left;
 * boxed page controls on the right with the active page filled `foreground`.
 * Controlled — every interaction calls `pagination.onPageChange`.
 */
export function DataTablePagination({
  pagination,
}: DataTablePaginationProps): React.ReactElement {
  const {
    page,
    pageSize,
    total,
    onPageChange,
    noun,
    totalPages: totalPagesProp,
    pageSizeOptions,
    onPageSizeChange,
    hideSummary,
    maxPageSize = Number.POSITIVE_INFINITY,
    keepWhenEmpty = false,
  } = pagination;
  // No rows → no footer, unless the caller keeps it (a search that matched
  // nothing still reads as a result, not a missing table).
  if (total === 0 && !keepWhenEmpty) return <></>;
  const totalPages = totalPagesProp ?? Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : Math.min((page - 1) * pageSize + 1, total);
  const to = Math.min(page * pageSize, total);
  const items = pageItems(page, totalPages);

  // Only offer sizes that fit the data; show the current size, or the show-all
  // choice when it exceeds every offered size.
  const sizeOptions = pageSizeOptions ? resolvePageSizeOptions(total, pageSizeOptions, maxPageSize) : [];
  const selectedSize = selectedPageSize(sizeOptions, pageSize);

  return (
    <div className="flex h-[52px] items-center justify-between border-t border-border px-4">
      <div className="flex items-center gap-3">
        {hideSummary ? (
          // `total` is not a real, known aggregate here (see the
          // `hideSummary` doc comment on DataTableProps) — never render a
          // confidently-wrong number, skeleton it instead.
          <Skeleton data-testid="pagination-summary-skeleton" className="h-4 w-32" />
        ) : (
          <span className="text-[12px] font-normal text-muted-foreground">
            Showing {from}–{to} of {total}
            {noun ? ` ${noun}` : ''}
          </span>
        )}
        {sizeOptions.length > 1 && onPageSizeChange && (
          <label className="flex items-center gap-1.5 text-[12px] font-normal text-muted-foreground">
            <span>Rows</span>
            <select
              value={selectedSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              aria-label="Rows per page"
              className="h-8 rounded-md border border-border bg-card px-2 text-[12px] font-medium text-foreground transition-colors hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {sizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      {totalPages > 1 && (
        <div className="flex items-center gap-1">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          className={cn(CONTROL, BORDERED, 'disabled:pointer-events-none disabled:opacity-40')}
          aria-label="Previous page"
        >
          ‹
        </button>
        {items.map((it, i) =>
          it === 'ellipsis' ? (
            <span
              key={`ellipsis-${i}`}
              className={cn(CONTROL, 'text-muted-foreground')}
              aria-hidden
            >
              …
            </span>
          ) : (
            <button
              key={it}
              type="button"
              onClick={() => onPageChange(it)}
              className={cn(CONTROL, it === page ? 'bg-foreground text-background' : BORDERED)}
              aria-label={`Page ${it}`}
              aria-current={it === page ? 'page' : undefined}
            >
              {it}
            </button>
          ),
        )}
        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          className={cn(CONTROL, BORDERED, 'disabled:pointer-events-none disabled:opacity-40')}
          aria-label="Next page"
        >
          ›
        </button>
        </div>
      )}
    </div>
  );
}
